import { useEffect, useRef, useState } from 'react';
import type { ProjectArtifactRow } from '../../shared/types';
import { api } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { Modal } from './primitives';

export interface ReviewArtifactActionsProps {
  artifact: ProjectArtifactRow;
  dashboardSlug?: string | null;
  configured?: boolean;
  mobile?: boolean;
  onUpdated?: (artifact: ProjectArtifactRow) => void;
  /** Keep an enclosing create modal open while confirming or saving a link. */
  onInteractionChange?: (active: boolean) => void;
}

/** Client sharing is separate from the row's embedded admin/editor Open action. */
export function ReviewArtifactActions({ artifact, dashboardSlug, configured = true, mobile = false, onUpdated, onInteractionChange }: ReviewArtifactActionsProps) {
  const phone = useIsMobile();
  const [current, setCurrent] = useState(artifact);
  const [busy, setBusy] = useState<'copy' | 'new-link' | 'sync-dashboard' | 'reload' | null>(null);
  const busyRef = useRef(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [manualCopy, setManualCopy] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const uncertainRotation = useRef<{ previousUrl: string | null } | null>(null);
  const opener = useRef<HTMLButtonElement>(null);
  useEffect(() => { setCurrent(artifact); }, [artifact]);
  useEffect(() => { onInteractionChange?.(Boolean(busy) || confirm); }, [busy, confirm, onInteractionChange]);

  if (artifact.tool !== 'review') return null;
  const linked = Boolean(current.external_id);
  const update = (row: ProjectArtifactRow, confirmed = false) => {
    if (confirmed || (uncertainRotation.current && row.public_url && row.public_url !== uncertainRotation.current.previousUrl)) uncertainRotation.current = null;
    const safe = uncertainRotation.current === null;
    setCurrent(row); setNeedsReload(!safe); onUpdated?.(row);
    return safe;
  };
  const reload = async () => {
    const result = await api<{ artifacts: ProjectArtifactRow[] }>(`/api/admin/projects/${artifact.project_id}/artifacts`, { cache: 'no-store' });
    const row = result.artifacts.find((item) => item.id === artifact.id);
    if (!row) throw new Error('The review is no longer linked to this project.');
    return update(row);
  };
  const retryReload = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('reload');
    try { const safe = await reload(); setError(safe ? null : 'The saved URL may be old. Use New link again to confirm a current link.'); setNotice(safe ? 'Saved review link refreshed.' : null); }
    catch { setError('Could not refresh the saved review. Retry before copying its link.'); }
    finally { busyRef.current = false; setBusy(null); }
  };
  const copy = async () => {
    if (busyRef.current || !current.public_url || needsReload) return;
    busyRef.current = true;
    setBusy('copy'); setError(null); setNotice(null);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(current.public_url);
      setManualCopy(false); setNotice('Client link copied.');
    } catch {
      setManualCopy(true);
      setError('Clipboard access was blocked. Select and copy the client link below.');
    } finally { busyRef.current = false; setBusy(null); }
  };
  const mutate = async (action: 'new-link' | 'sync-dashboard') => {
    if (busyRef.current || !linked || !configured || (action === 'sync-dashboard' && needsReload)) return;
    busyRef.current = true;
    setBusy(action); setError(null); setNotice(null); setManualCopy(false);
    try {
      const result = await api<{ artifact: ProjectArtifactRow }>(
        `/api/admin/projects/${artifact.project_id}/artifacts/${artifact.id}/${action}`,
        { method: 'POST', json: {}, cache: 'no-store' },
      );
      if (!result.artifact || result.artifact.id !== artifact.id) throw new Error('The saved review could not be refreshed.');
      update(result.artifact, true);
      setConfirm(false);
      setNotice(action === 'sync-dashboard' ? 'Review link synced to the dashboard.' :
        dashboardSlug ? 'New client link saved and dashboard updated. No email sent.' : 'New client link saved. No email sent.');
    } catch (cause) {
      setConfirm(false);
      const message = cause instanceof Error ? cause.message : 'Review sharing failed. Retry shortly.';
      // Rotation can commit before dashboard publication fails. Read the saved
      // row before allowing copy, instead of leaving a stale URL on screen.
      if (action === 'new-link') uncertainRotation.current = { previousUrl: current.public_url };
      setNeedsReload(true);
      try { const safe = await reload(); setError(safe ? message : `${message} The saved URL may be old. Use New link again to confirm a current link.`); }
      catch { setError(`${message} Could not refresh the saved review. Retry before copying its link.`); }
    } finally { busyRef.current = false; setBusy(null); }
  };
  const closeConfirm = () => {
    if (busyRef.current) return;
    setConfirm(false);
    opener.current?.focus();
  };
  const buttonStyle = { minHeight: mobile || phone ? 44 : 30, padding: '0 9px', fontSize: 11, whiteSpace: 'normal' as const };

  return <div style={{ gridColumn: '1 / -1', minWidth: 0, marginTop: 6 }} data-review-sharing={artifact.id}>
    <div role="group" aria-label={`Review sharing for ${current.title ?? 'Review'}`} aria-busy={Boolean(busy)}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <button type="button" className="btn" style={buttonStyle} onClick={copy}
        disabled={Boolean(busy) || !current.public_url || needsReload}>Copy client link</button>
      <button ref={opener} type="button" className="btn" style={buttonStyle} onClick={() => { setError(null); setConfirm(true); }}
        disabled={Boolean(busy) || !linked || !configured}>New link</button>
      {dashboardSlug && <button type="button" className="btn" style={buttonStyle} onClick={() => void mutate('sync-dashboard')}
        disabled={Boolean(busy) || !linked || !configured || !current.public_url || needsReload}>Retry dashboard sync</button>}
    </div>
    {busy && <p role="status" style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
      {busy === 'new-link' ? 'Saving a new review link…' : busy === 'sync-dashboard' ? 'Syncing the dashboard…' : busy === 'copy' ? 'Copying…' : 'Refreshing the saved review…'}
    </p>}
    {!current.public_url && !busy && <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>No client link saved yet. Use New link to finish sharing.</p>}
    {!configured && <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>Review tool integration is not configured.</p>}
    {notice && <p role="status" style={{ fontSize: 11, color: 'var(--accent)', marginTop: 6 }}>{notice}</p>}
    {error && <p className="error-text" role="alert" style={{ fontSize: 11, marginTop: 6 }}>{error}</p>}
    {needsReload && <button type="button" className="btn" style={buttonStyle} disabled={Boolean(busy)} onClick={retryReload}>Refresh saved link</button>}
    {manualCopy && current.public_url && !needsReload && <label style={{ display: 'block', marginTop: 6, fontSize: 11 }}>
      Client link<input aria-label="Client link" className="f-input mono" readOnly value={current.public_url} onFocus={(event) => event.currentTarget.select()} />
    </label>}
    {confirm && <Modal title={mobile || phone ? (current.public_url ? 'Replace link?' : 'Create link?') : (current.public_url ? 'Replace this review link?' : 'Create a client review link?')} context={current.title ?? 'REVIEW'}
      mobile="half" onClose={closeConfirm} foot={<>
        <button type="button" className="btn" autoFocus disabled={Boolean(busy)} onClick={closeConfirm}>{current.public_url && !mobile && !phone ? 'Keep current link' : 'Cancel'}</button>
        <button type="button" className="btn primary" disabled={Boolean(busy)} onClick={() => void mutate('new-link')}>
          {busy ? 'Saving…' : current.public_url ? 'Replace link' : 'Create link'}
        </button>
      </>}>
      <p>{current.public_url ? 'The current review URL will stop working. Anyone using the old link will need the new URL.' : 'Create a client link for this saved review.'}</p>
      <p style={{ color: 'var(--text-dim)', marginTop: 12 }}>The new link is saved on this project{dashboardSlug ? ' and the client dashboard' : ''}. Creating a new link does not send an email.</p>
    </Modal>}
  </div>;
}
